import pino from 'pino';
import os from 'os';
import { LogFormatter } from './utils/logFormatter';

type LogParams = Record<string, any>;

interface LogOptions {
  isFullLog?: boolean;
}

class StructuredLogger {
  private logger: pino.Logger;
  private defaultOptions: LogOptions = {
    isFullLog: false
  };

  constructor(baseLogger?: pino.Logger, options?: LogOptions) {
    this.logger = baseLogger || pino({
      level: process.env.NODE_ENV === 'development' ? 'info' : 'info',
      formatters: {
        level: (label) => {
          return { level: label.toUpperCase() };
        },
      },
      timestamp: pino.stdTimeFunctions.isoTime,
      serializers: {
        error: pino.stdSerializers.err,
        params: (value) => {
          if (typeof value === 'object' && value !== null) {
            return JSON.stringify(value, null, 2);
          }
          return value;
        }
      }
    });
  }

  // Support for child loggers with additional context
  child(bindings: Record<string, any>): StructuredLogger {
    const level = bindings.level || this.logger.level;
    delete bindings.level; // Remove level from bindings as it's handled separately
    
    const childLogger = this.logger.child(bindings);
    if (level === 'silent') {
      childLogger.level = 'silent';
    }
    
    return new StructuredLogger(childLogger);
  }

  private isError(value: unknown): value is Error {
    return value instanceof Error || (
      typeof value === 'object' && 
      value !== null && 
      'message' in value &&
      'name' in value
    );
  }

  private normalizeError(error: unknown): Error {
    if (this.isError(error)) {
      return error;
    }
    if (typeof error === 'string') {
      return new Error(error);
    }
    return new Error(JSON.stringify(error));
  }

  private formatValue(value: unknown): string {
    if (typeof value === 'object' && value !== null) {
      try {
        return '\n' + JSON.stringify(value, null, 2);
      } catch (err) {
        return String(value);
      }
    }
    return String(value);
  }

  private normalizeArgs(...args: unknown[]): { 
    message: string; 
    params: LogParams;
  } {
    // If no arguments, return empty
    if (args.length === 0) {
      return { message: '', params: {} };
    }

    // Handle single argument cases
    if (args.length === 1) {
      const [arg] = args;
      if (typeof arg === 'string') {
        return { message: arg, params: {} };
      }
      if (typeof arg === 'object' && arg !== null) {
        return { message: '', params: arg as LogParams };
      }
      return { message: String(arg), params: {} };
    }

    // Handle multiple arguments
    const firstArg = args[0];
    const secondArg = args[1];
    const remainingArgs = args.slice(2);

    // Case: logger.info('message', { params })
    if (typeof firstArg === 'string' && typeof secondArg === 'object' && secondArg !== null && !Array.isArray(secondArg) && !this.isError(secondArg) && remainingArgs.length === 0) {
      return { message: firstArg, params: secondArg as LogParams };
    }

    // Case: logger.info({ params }, 'message')
    if (typeof firstArg === 'object' && firstArg !== null && !Array.isArray(firstArg) && !this.isError(firstArg) && typeof secondArg === 'string' && remainingArgs.length === 0) {
      return { message: secondArg, params: firstArg as LogParams };
    }

    // Case: logger.debug('Capturing event:', eventName, properties)
    // Convert additional arguments into params
    if (typeof firstArg === 'string') {
      const params: LogParams = {};
      const allArgs = [secondArg, ...remainingArgs];
      
      allArgs.forEach((arg, index) => {
        if (typeof arg === 'object' && arg !== null && !Array.isArray(arg)) {
          Object.assign(params, arg);
        } else {
          params[`arg${index + 1}`] = this.formatValue(arg);
        }
      });

      return { message: firstArg, params };
    }

    // Default case - convert everything to params
    const params: LogParams = {};
    args.forEach((arg, index) => {
      if (typeof arg === 'object' && arg !== null && !Array.isArray(arg)) {
        Object.assign(params, arg);
      } else {
        params[`arg${index}`] = arg;
      }
    });

    return { message: '', params };
  }

  private formatLog(logEntry: Record<string, any>, options?: LogOptions): string {
    const finalOptions = { ...this.defaultOptions, ...options };
    return LogFormatter.formatToString(logEntry, finalOptions.isFullLog);
  }

  info(...args: unknown[]) {
    const { message, params } = this.normalizeArgs(...args);
    const logEntry = {
      ...params,
      _formatted: true,
      time: new Date().toISOString(),
      level: 'INFO',
      msg: message
    };
    
    if (process.env.NODE_ENV === 'development') {
      console.log(this.formatLog(logEntry));
    }
    
    this.logger.info(params, message);
  }

  error(...args: unknown[]) {
    let { message, params } = this.normalizeArgs(...args);
    
    const error = args.find(arg => this.isError(arg));
    if (error) {
      const normalizedError = this.normalizeError(error);
      params = {
        ...params,
        error: {
          message: normalizedError.message,
          stack: normalizedError.stack?.split('\n'),
          name: normalizedError.name,
        },
      };
    }

    const logEntry = {
      ...params,
      _formatted: true,
      time: new Date().toISOString(),
      level: 'ERROR',
      msg: message
    };

    if (process.env.NODE_ENV === 'development') {
      console.error(this.formatLog(logEntry, { isFullLog: true }));
    }

    this.logger.error(params, message);
  }

  warn(...args: unknown[]) {
    const { message, params } = this.normalizeArgs(...args);
    const logEntry = {
      ...params,
      _formatted: true,
      time: new Date().toISOString(),
      level: 'WARN',
      msg: message
    };

    if (process.env.NODE_ENV === 'development') {
      console.warn(this.formatLog(logEntry));
    }

    this.logger.warn(params, message);
  }

  debug(...args: unknown[]) {
    const { message, params } = this.normalizeArgs(...args);
    const logEntry = {
      ...params,
      _formatted: true,
      time: new Date().toISOString(),
      level: 'DEBUG',
      msg: message
    };

    if (process.env.NODE_ENV === 'development') {
      console.debug(this.formatLog(logEntry));
    }

    this.logger.debug(params, message);
  }

  // Development only console methods
  devLog(...args: unknown[]) {
    if (process.env.NODE_ENV === 'development') {
      const { message, params } = this.normalizeArgs(...args);
      console.log('[DEV]', params, message);
    }
  }

  devError(...args: unknown[]) {
    if (process.env.NODE_ENV === 'development') {
      const { message, params } = this.normalizeArgs(...args);
      const error = args.find(arg => this.isError(arg));
      if (error) {
        const normalizedError = this.normalizeError(error);
        console.error('[DEV]', { ...params, error: normalizedError }, message);
      } else {
        console.error('[DEV]', params, message);
      }
    }
  }
}

export const logger = new StructuredLogger();

// Usage examples (can be removed):
/*
// All these patterns now work:
logger.info('Simple message');
logger.info('Message with params', { userId: '123' });  // Original style
logger.info({ userId: '123' }, 'Message with params');  // New style
logger.info({ userId: '123', message: 'Inline message' });  // Object only

// Multiple arguments are automatically converted to params
logger.debug('Capturing event:', 'click', { x: 100, y: 200 });
// Becomes: { message: 'Capturing event:', params: { arg1: 'click', x: 100, y: 200 } }

logger.debug('User action:', 'login', 'success', { userId: '123' });
// Becomes: { message: 'User action:', params: { arg1: 'login', arg2: 'success', userId: '123' } }

// Error logging patterns
logger.error('Error occurred', new Error('Something went wrong'));
logger.error('Error with context', { userId: '123' }, new Error('Something went wrong'));
logger.error({ userId: '123' }, 'Error message', new Error('Something went wrong'));
logger.error('Failed to fetch', unknownError);  // Works with unknown error types
logger.error('API Error', { status: 500 }, someUnknownError);  // Works with params and unknown errors

// Child logger
const userLogger = logger.child({ userId: '123', context: 'user-service' });
userLogger.info('User action');
userLogger.info('User action with params', { action: 'login' });
userLogger.info({ action: 'login' }, 'User action with params');
*/
